<template>
  <div :class="[$style.config, $style.wrap, isModal && $style.inModal]">
    <div :class="[$style.control, $style.controlPrev]">
      <button
        :class="$style.prevButton"
        :aria-label="localizedStrings['previous'] || 'Previous'"
        @click="handleNavigationSlideChange('prev')"
      >
        <IconChevronLeft :class="$style.chevronLeft" />
      </button>
    </div>
    <div :class="$style.media">
      <div ref="swiper" :class="['swiper', $style.swiper]">
        <div :class="'swiper-wrapper'">
          <div
            v-for="image in data"
            :key="image?.sys?.id"
            :class="['swiper-slide', $style.swiperSlide]"
          >
            <div :class="$style.imageWrapper" @click="emit('open-modal')">
              <BasePicture
                :src="`${image?.fields?.file?.url}?w=1310&fit=thumb&q=70`"
                :height="image?.fields?.file?.details?.image?.height"
                :width="image?.fields?.file?.details?.image?.width"
                :class="$style.picture"
                alt=""
                loading="lazy"
              />
              <div
                v-if="image?.fields?.description"
                :class="$style.imageDescription"
              >
                {{ image?.fields?.description }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="[$style.control, $style.controlNext]">
      <button
        :class="$style.nextButton"
        :aria-label="localizedStrings['next'] || 'Next'"
        @click="handleNavigationSlideChange('next')"
      >
        <IconChevronRight :class="$style.chevronRight" />
      </button>
    </div>
    <div :class="$style.pagination">
      <div
        v-for="(image, index) in data"
        :key="`pagination-${image?.sys?.id}`"
        :class="[$style.dot, activeIndex === index && $style.active]"
        @click="handleSetActiveSlide(index)"
      />
    </div>
    <div :class="$style.thumbnails">
      <div
        v-for="(image, index) in data"
        :key="image?.sys?.id"
        :class="[
          $style.thumbnail,
          index === activeIndex && $style.thumbnailActive,
        ]"
        @click="handleSetActiveSlide(index)"
      >
        <AspectRatio ratio="242x137">
          <BasePicture
            :src="`${image?.fields?.file?.url}?w=242&h=137&fit=pad`"
            :class="$style.thumbnailPicture"
            width="242"
            height="137"
            alt=""
          />
        </AspectRatio>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Swiper from 'swiper'

import type { Asset } from 'contentful'

import 'swiper/css'

const { localizedStrings } = useLocalizedStrings()

const emit = defineEmits(['open-modal'])

const props = defineProps<{
  data: (Asset<'WITHOUT_UNRESOLVABLE_LINKS'> | undefined)[] | undefined
  isModal?: boolean
}>()

// Data
const swiperElRef = useTemplateRef<HTMLElement>('swiper')
const swiperInstance = ref<null | Swiper>(null)
const activeIndex = ref(0)

// Watchers
watch(activeIndex, (index) => {
  swiperInstance?.value?.slideToLoop?.(index)
})

// Methods
const handleNavigationSlideChange = (direction: string) => {
  const hasPrevSlide = activeIndex.value > 0
  const hasNextSlide = activeIndex.value < (props?.data?.length || 0) - 1
  if (direction === 'prev') {
    activeIndex.value = hasPrevSlide
      ? activeIndex.value - 1
      : (props?.data?.length || 0) - 1
  }
  if (direction === 'next') {
    activeIndex.value = hasNextSlide
      ? activeIndex.value + 1
      : (activeIndex.value = 0)
  }
}

const handleSetActiveSlide = (index: number) => (activeIndex.value = index)

const initilizeSwiper = () => {
  if (swiperElRef.value) {
    swiperInstance.value = new Swiper(swiperElRef.value, {
      loop: true,
      autoHeight: true,
    })
  }
}

onMounted(() => {
  if (!props.isModal) initilizeSwiper()
})
</script>

<style lang="scss" module>
.config {
  --thumbnail-gap: 5px;
  --description-width: 100%;

  @include media('lg') {
    --thumbnail-gap: 20px;
    --description-width: 80%;
  }
}

.wrap {
  align-items: center;
  display: grid;
  grid-template-areas: 'controlPrev media controlNext' 'pagination pagination pagination' 'thumbnails thumbnails thumbnails';
  grid-template-rows: minmax(0, 1fr) max-content max-content;
  gap: 1.5rem 40px;

  & > * {
    min-width: 0;
  }

  &.inModal {
    height: 100%;
    padding: 1.5rem;
  }
}

.controlPrev {
  grid-area: controlPrev;
}

.media {
  grid-area: media;

  .inModal & {
    height: 100%;
    min-height: 0;
  }
}

.swiper {
  height: 100%;
}

.imageWrapper {
  height: 100%;
}

.picture {
  margin-bottom: 0.5em;
  text-align: center;
  width: 100%;
  height: 100%;
  display: block;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.controlNext {
  grid-area: controlNext;
}

.prevButton,
.nextButton {
  align-items: center;
  background-color: var(--purple);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 39px;
  justify-content: center;
  pointer-events: auto;
  width: 39px;
}

.chevronLeft,
.chevronRight {
  color: var(--white);
  height: 13px;
  width: 8px;
}

.pagination {
  align-items: center;
  display: flex;
  justify-content: center;
  grid-area: pagination;
}

.dot {
  background-color: transparent;
  border: 1px solid var(--purple-light);
  border-radius: 50%;
  cursor: pointer;
  height: 11px;
  margin: 0 6px;
  width: 11px;

  &.active {
    background-color: var(--purple-light);
  }
}

.thumbnails {
  grid-area: thumbnails;
}

// .imageDescription {
//   font-size: to-rem(16px);
//   line-height: math.div(24, 16);
//   margin: 0 auto 1em;
//   text-align: center;
//   width: var(--description-width);
// }

.thumbnails {
  display: grid;
  gap: var(--thumbnail-gap);
  grid-template-columns: repeat(5, 1fr);
  margin: auto;
  max-width: 1300px;
}

.thumbnail {
  border: 1px solid var(--gray-4);
  cursor: pointer;

  .inModal & {
    background-color: var(--white);
  }
}

.thumbnailPicture {
  filter: opacity(0.3);
  transition: filter 0.25s;

  .thumbnailActive & {
    filter: opacity(1);
  }
}
</style>
